import { z } from 'zod';
import { request } from '@/lib/api';
import i18n from '@/lib/i18n';
import { tradeSchema } from '@/lib/schemas';

/* ---------------------------------------------------------------------------
   פרטי העסק וברירות המחדל להצעת מחיר (GET/PUT /settings/business).
   הסכמה המלאה מאומתת בשרת (src/modules/tenants/business-settings.ts).
   --------------------------------------------------------------------------- */

export const paymentTermsSchema = z.object({
  schedule: z.array(z.object({ label: z.string(), percent: z.number() })),
  methods: z.array(z.string()),
  notes: z.string(),
});
export type PaymentTerms = z.infer<typeof paymentTermsSchema>;

export const businessSettingsSchema = z.object({
  trade: tradeSchema,
  locale: z.enum(['he', 'en']).catch('he'),
  features: z.array(z.string()).optional(),
  profile: z.object({
    legalName: z.string(),
    taxId: z.string(),
    phone: z.string(),
    email: z.string(),
    address: z.string(),
  }),
  quoteDefaults: z.object({
    validDays: z.number(),
    vatMode: z.enum(['added', 'included', 'exempt']),
    vatRate: z.number(),
    paymentTerms: paymentTermsSchema,
    termsText: z.string(),
    afterApproval: z
      .object({ plan: z.boolean(), autoSchedule: z.boolean(), materialsLeadDays: z.number(), workdayHours: z.number() })
      .catch({ plan: true, autoSchedule: true, materialsLeadDays: 2, workdayHours: 8 }),
  }),
  /** סעיפים מוצעים לאימוץ בלחיצה — ההצעות לא כוללות סעיפים עד שהבעלים מאמץ. */
  suggestedTerms: z.string().catch(''),
});
export type BusinessSettings = z.infer<typeof businessSettingsSchema>;

export const BUSINESS_SETTINGS_KEY = ['settings', 'business'];

export const businessApi = {
  get: (signal?: AbortSignal) => request('/settings/business', { schema: businessSettingsSchema, signal }),
  update: ({ suggestedTerms: _suggested, ...body }: Partial<BusinessSettings>) =>
    request('/settings/business', { method: 'PUT', body, schema: businessSettingsSchema }),
};

// אמצעי התשלום נשמרים כטקסט בהצעה (ומוצגים ללקוח) — בשפת העסק.
export const PAYMENT_METHODS = (['bank', 'card', 'bit', 'paybox', 'check', 'cash'] as const).map((k) =>
  i18n.t(`settings:paymentMethods.${k}`),
);

export const scheduleTotal = (t: PaymentTerms) => t.schedule.reduce((sum, s) => sum + (Number(s.percent) || 0), 0);
